/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useMemo } from 'react';
import IndicatorSelector from './IndicatorSelector';
import { Modal } from '../../ui/modal';

// Minimal builder for window predicates over boolean series
// value shape (UI): { kind: 'window_pred', op: 'ANY_TRUE_LAST_N'|'ALL_TRUE_LAST_N'|'COUNT_TRUE_LAST_N'|'OCCURRED_WITHIN_N', inputRef: string|object, n: number }
// Serialization layer will map to backend: { op, expr: <boolean-expr>, n }

const OPS = [
  { value: 'ANY_TRUE_LAST_N', label: 'any_true_in_last_n' },
  { value: 'ALL_TRUE_LAST_N', label: 'all_true_in_last_n' },
  { value: 'COUNT_TRUE_LAST_N', label: 'count_true_last_n' },
  { value: 'OCCURRED_WITHIN_N', label: 'occurred_within' },
];

export default function WindowPredicateBuilder({ value, onChange, booleanSources = [], baseTimeframe }) {
  const v = value || { kind: 'window_pred', op: 'ANY_TRUE_LAST_N', inputRef: '', n: 3 };
  const update = (patch) => onChange?.({ ...v, ...patch });
  const isInlineObject = typeof v.inputRef === 'object' && v.inputRef !== null;
  const isComparator = isInlineObject && v.inputRef.lhs && v.inputRef.op && (v.inputRef.rhs !== undefined);
  const sourceMode = isInlineObject ? 'inline' : 'preset';

  // Build a safe default comparator if switching to inline and none exists yet
  const ensureComparator = () => {
    if (isComparator) return v.inputRef;
    const defaultLhs = isInlineObject && v.inputRef?.type === 'indicator'
      ? v.inputRef
      : { type:'indicator', name:'ema', params:{ length: 21 }, input:{ type:'price', source:'close' } };
    return { lhs: defaultLhs, op: 'greater_than', rhs: 0 };
  };

  const inlineExpr = sourceMode === 'inline' ? ensureComparator() : null;
  const setInlineExpr = (expr) => update({ inputRef: expr });

  // RHS kind: 'number' or 'indicator'
  const rhsKind = inlineExpr && typeof inlineExpr.rhs === 'object' ? 'indicator' : 'number';

  // Modal state for editing comparator cleanly
  const [open, setOpen] = useState(false);
  const summary = useMemo(() => {
    if (!inlineExpr) return '—';
    const lhs = inlineExpr.lhs?.name || (inlineExpr.lhs?.type === 'price' ? 'price' : 'lhs');
    const rhs = typeof inlineExpr.rhs === 'object' ? (inlineExpr.rhs?.name || 'indicator') : String(inlineExpr.rhs ?? 0);
    const op = inlineExpr.op || 'greater_than';
    return `${lhs} ${op} ${rhs}`;
  }, [inlineExpr]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 items-center">
        {/* Window op */}
        <select
          className="h-9 text-sm rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary"
          value={v.op}
          onChange={(e)=>update({ op: e.target.value })}
        >
          {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {/* Boolean source: preset or inline comparator */}
        {sourceMode === 'preset' ? (
          <select
            className="h-9 text-sm rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary"
            value={typeof v.inputRef === 'string' ? v.inputRef : ''}
            onChange={(e)=>update({ inputRef: e.target.value })}
          >
            <option value="" disabled>Boolean input</option>
            {booleanSources.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        ) : (
          <div className="col-span-1 flex items-center gap-2">
            <div className="text-xs text-textSecondary truncate max-w-[180px]" title={summary}>{summary}</div>
            <button type="button" className="h-8 px-2 text-xs rounded border border-borderDefault hover:bg-muted" onClick={()=> setOpen(true)}>Edit</button>
          </div>
        )}

        {/* Lookback size */}
        <input
          type="number"
          min={1}
          step={1}
          className="h-9 text-sm rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary placeholder-textSecondary w-full"
          value={Number.isFinite(v.n) ? v.n : 1}
          onChange={(e)=>update({ n: Math.max(1, parseInt(e.target.value||'1',10)) })}
          placeholder="Bars"
        />
      </div>

      {/* Source mode toggle */}
      <div className="flex items-center gap-2 text-[11px] text-textSecondary">
        <span>Source:</span>
        <button
          type="button"
          className="px-1.5 py-0.5 rounded border border-borderDefault hover:bg-muted/50"
          onClick={() => {
            if (sourceMode === 'preset') {
              update({ inputRef: ensureComparator() });
            } else {
              update({ inputRef: '' });
            }
          }}
        >
          {sourceMode === 'preset' ? 'Switch to inline comparator' : 'Switch to preset list'}
        </button>
      </div>

      {/* Hint: COUNT returns number; others return boolean */}
      <div className="text-[11px] text-textSecondary">
        {v.op === 'COUNT_TRUE_LAST_N' ? 'Returns a number; compare with >=, >, ==, etc.' : 'Returns boolean; can be used in AND/OR groups.'}
      </div>

      {/* Modal for inline comparator editing */}
      <Modal open={open} onClose={()=> setOpen(false)} title="Edit Window Condition" maxWidthClass="max-w-4xl">
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3">
            {/* LHS */}
            <div>
              <div className="text-xs mb-1 text-textSecondary">Left-hand side</div>
              <IndicatorSelector
                dense
                value={inlineExpr?.lhs || { type:'indicator', name:'ema', params:{ length: 21 }, input:{ type:'price', source:'close' } }}
                onChange={(next)=> setInlineExpr({ ...inlineExpr, lhs: next })}
                baseTimeframe={baseTimeframe}
              />
            </div>
            {/* Operator + RHS kind */}
            <div className="flex flex-col items-center justify-center gap-2">
              <select
                className="h-9 text-sm rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary"
                value={inlineExpr?.op || 'greater_than'}
                onChange={(e) => {
                  const op = e.target.value;
                  const needsIndicator = op === 'crosses_above' || op === 'crosses_below';
                  const nextRhs = needsIndicator && rhsKind === 'number' ? { type:'indicator', name:'ema', params:{ length: 50 }, input:{ type:'price', source:'close' } } : inlineExpr?.rhs;
                  setInlineExpr({ ...inlineExpr, op, rhs: nextRhs });
                }}
              >
                <option value="greater_than">&gt;</option>
                <option value="less_than">&lt;</option>
                <option value="greater_than_or_equal">&gt;=</option>
                <option value="less_than_or_equal">&lt;=</option>
                <option value="equals">==</option>
                <option value="not_equals">!=</option>
                <option value="crosses_above">crosses above</option>
                <option value="crosses_below">crosses below</option>
              </select>
        <div className="inline-flex items-center p-0.5 rounded border border-borderDefault">
                {['indicator','number'].map((k) => {
                  const disabled = (inlineExpr?.op === 'crosses_above' || inlineExpr?.op === 'crosses_below') && k === 'number';
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={disabled}
                      onClick={() => setInlineExpr({ ...inlineExpr, rhs: k === 'indicator' ? { type:'indicator', name:'ema', params:{ length: 50 }, input:{ type:'price', source:'close' } } : (Number.isFinite(inlineExpr?.rhs) ? inlineExpr.rhs : 0) })}
          className={`px-2 py-0.5 text-xs rounded ${rhsKind === k ? 'bg-backgroundSurface text-textPrimary shadow-sm' : 'text-textSecondary'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {k}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* RHS */}
            <div>
              <div className="text-xs mb-1 text-textSecondary">Right-hand side</div>
              {rhsKind === 'indicator' ? (
                <IndicatorSelector
                  dense
                  value={typeof inlineExpr?.rhs === 'object' ? inlineExpr.rhs : { type:'indicator', name:'ema', params:{ length: 50 }, input:{ type:'price', source:'close' } }}
                  onChange={(next) => setInlineExpr({ ...inlineExpr, rhs: next })}
                  baseTimeframe={baseTimeframe}
                />
              ) : (
                <input
                  type="number"
                  className="h-9 w-32 text-sm rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary placeholder-textSecondary px-2"
                  value={Number.isFinite(inlineExpr?.rhs) ? inlineExpr.rhs : 0}
                  onChange={(e)=> setInlineExpr({ ...inlineExpr, rhs: Number(e.target.value) })}
                />
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button type="button" className="h-9 px-3 rounded border border-borderDefault hover:bg-muted" onClick={()=> setOpen(false)}>Done</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
